import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { List, Map } from 'immutable';
import { updateQueueProfile } from 'flow/actions/pbx-actions';
import { MiniAudioPlayer } from 'flow/components/AudioPlayers';
import Page from 'flow/components/Setting/Page';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import Loading from 'flow/components/ui-components/Loading';
import SettingSelectableItem from 'flow/components/ui-components/SettingSelectableItem';
import useAppContext from 'flow/hooks/useAppContext';
import { PbxParams } from 'flow/types/Params';
import Queue from 'flow/types/Queue';
import { routes } from 'flow/types/routes';
import { Sound } from 'flow/types/Sound';

import { TypedMap } from 'flow/types/TypedMap';
import {
    homeActionUrlPath,
    isUserSoundKey,
    SOUND_PLAYER_ANUMBER,
} from 'flow/utils';
import { getWelcomeSounds } from 'flow/utils/pbx-utils';

const PbxWelcomeMessageClosedProfileSound = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { i18next } = useAppContext();

    const { entityKey, profileKey } = useParams<PbxParams>();

    const pbxDetails: TypedMap<Queue> = useAppSelector((state) =>
        state.pbx.getIn(['queues', entityKey], Map()),
    );

    const sounds = useAppSelector((state) => state.sounds);

    const profiles = pbxDetails.get('profiles');
    const defaultSounds = sounds.get('defaultSounds', Map());

    if (!pbxDetails?.has('settings') || !defaultSounds || !profiles) {
        return <Loading />;
    }

    const handleSound = (userSoundKey: string) => {
        const profile = profiles.find(
            (profile) => profile.get('key') === profileKey,
        );

        if (isUserSoundKey(userSoundKey)) {
            const allSounds: TypedMap<Sound> = queueSounds
                .concat(defaultWelcomeSounds)
                .toOrderedSet()
                .toList();

            const selectedSound = allSounds.find(
                (sound) => sound.get('key') === userSoundKey,
            );

            const number =
                SOUND_PLAYER_ANUMBER + selectedSound.get('recordingPIN');

            const newProfile = profile.setIn(
                ['callForwarding', 'redirect', 'number'],
                number,
            );
            dispatch(updateQueueProfile(entityKey, profileKey, newProfile));
        } else {
            const newProfile = profile.setIn(
                ['callForwarding', 'redirect'],
                {},
            );
            dispatch(updateQueueProfile(entityKey, profileKey, newProfile));
        }
        history.push(
            generatePath(
                routes[
                    'pbx.queues.entityKey.settings.welcomemessage.profileKey'
                ],
                { entityKey, profileKey },
            ),
        );
    };

    const userSounds = sounds.get('sounds', List());

    const queueSounds = getWelcomeSounds(pbxDetails, userSounds, defaultSounds);
    const profile = profiles?.find(
        (profile) => profile.get('key') === profileKey,
    );

    const callForwarding = profile.get('callForwarding');
    const redirectNumber = callForwarding.has('redirect')
        ? callForwarding.getIn(['redirect', 'number'])
        : null;

    const defaultWelcomeSounds = sounds
        ? pbxDetails.getIn(['settings', 'defaultWelcomeSounds'], Map()).first()
        : null;

    const endCallWithSound = defaultWelcomeSounds
        ? queueSounds.concat(defaultWelcomeSounds).toOrderedSet().toList()
        : null;

    let selectedSound = null;
    if (redirectNumber) {
        const recordingPIN = redirectNumber.split(SOUND_PLAYER_ANUMBER)[1];
        selectedSound = queueSounds
            .filter((sound: any) => sound.get('recordingPIN') === recordingPIN)
            .first();
    }
    const selectedSoundKey = selectedSound ? selectedSound.get('key') : null;

    return (
        <Page
            header="pbx.closed"
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes[
                            'pbx.queues.entityKey.settings.welcomemessage.profileKey'
                        ],
                        {
                            entityKey,
                            profileKey,
                        },
                    ),
                )
            }
        >
            <div className="text-xs text-gray-500">
                {i18next.t(
                    'Queue.WelcomeMessage.Unavailable.SoundPickerDescription',
                )}
            </div>

            <div className="gap-4">
                <SettingSelectableItem
                    value={null}
                    onClick={() => handleSound('none')}
                    selected={selectedSoundKey}
                >
                    <div className="flex items-center gap-1">
                        <div>
                            <IconButton
                                onClick={() => handleSound('none')}
                                aria-label={i18next.t('empty.sound')}
                            >
                                <Icon iconKey="volumeOff" />
                            </IconButton>
                        </div>

                        <div>{i18next.t('empty.sound')}</div>
                    </div>
                </SettingSelectableItem>

                {endCallWithSound.map((soundDTO: TypedMap<Sound>) => {
                    if (!soundDTO.has('playbackURL')) {
                        return null;
                    }

                    const playbackUrl = homeActionUrlPath(
                        `/${soundDTO.get('playbackURL')}`,
                    );
                    return (
                        <SettingSelectableItem
                            key={soundDTO.get('key')}
                            value={soundDTO.get('key')}
                            onClick={() => handleSound(soundDTO.get('key'))}
                            selected={selectedSoundKey}
                        >
                            <div className="flex items-center gap-1">
                                <div>
                                    {playbackUrl ? (
                                        <MiniAudioPlayer
                                            url={playbackUrl}
                                            uniqueId={`user-sound-key${soundDTO.get(
                                                'key',
                                            )}`}
                                        />
                                    ) : null}
                                </div>

                                <div>
                                    {soundDTO.get('name')} -{' '}
                                    {soundDTO.get('key')}
                                </div>
                            </div>
                        </SettingSelectableItem>
                    );
                })}
            </div>
        </Page>
    );
};

export default PbxWelcomeMessageClosedProfileSound;
